<template>
    <!-- SSH 网页控制台：终端抽屉 -->
    <!-- close-on-click-modal=false：点遮罩（空白处）不关闭。误点会直接杀掉远端进程（刷新/断开等于关终端窗口），代价太高 -->
    <el-drawer v-model="drawerVisible" class="ssh-console-drawer" direction="btt" :show-close="false"
        :close-on-click-modal="false" :before-close="handleBeforeClose" size="85%">
        <template #header="{ close, titleId, titleClass }">
            <div :id="titleId" :class="[titleClass, 'ssh-console-head']">
                <span class="ssh-console-head__machine">{{ machine.name }}</span>
                <span class="ssh-console-head__addr">{{ machine.address }}</span>
                <span v-if="recording" class="ssh-console-head__rec">● 录制中</span>
                <span class="ssh-console-head__status" :class="statusClass">{{ statusText }}</span>
            </div>
            <div class="ssh-console-head__ops">
                <!-- 文件面板开关：开控制台之后想传/下文件，就地展开，终端始终可见 -->
                <el-button class="ssh-console-btn" :class="{ 'is-on': filePanelVisible }" size="small"
                    @click="filePanelVisible = !filePanelVisible">
                    <el-icon class="ssh-console-btn__icon">
                        <FolderOpened />
                    </el-icon>
                    文件
                </el-button>
                <el-button class="ssh-console-btn" size="small" :disabled="status === 'connecting'" @click="reconnect">
                    重连
                </el-button>
                <el-button class="ssh-console-btn" size="small" type="danger" plain @click="close">关闭</el-button>
            </div>
        </template>
        <!-- 横向分栏：终端占满剩余宽度，文件面板按需展开在右侧 -->
        <div class="ssh-console-body">
            <div ref="termHost" class="ssh-console-term"></div>
            <ssh-file-panel v-if="filePanelVisible" :machine="machine" @close="filePanelVisible = false" />
        </div>
    </el-drawer>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { config } from '@/utils/config';
import SshFilePanel from './SshFilePanel.vue';

export default {
    name: 'SshConsoleDrawer',
    components: { SshFilePanel },
    props: {
        visible: { type: Boolean, default: false },
        machine: { type: Object, default: () => ({}) },
        token: { type: String, default: '' }
    },
    emits: ['update:visible'],
    data() {
        return {
            ws: null,
            term: null,
            fitAddon: null,
            resizeObserver: null,
            fitTimer: null,
            // 本次会话是否在录制（由后端 ready 帧告知）
            recording: false,
            // 右侧文件面板是否展开（展开/收起会改变终端宽度，ResizeObserver 会自动把新尺寸同步给 PTY）
            filePanelVisible: false,
            // closed | connecting | connected
            status: 'closed'
        };
    },
    computed: {
        drawerVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit('update:visible', value);
            }
        },
        statusText() {
            return { closed: '已断开', connecting: '连接中…', connected: '已连接' }[this.status] || '';
        },
        statusClass() {
            return `is-${this.status}`;
        }
    },
    watch: {
        visible(value) {
            if (value) {
                // 等抽屉 DOM 真正渲染出来再挂载终端
                this.$nextTick(() => this.start());
            } else {
                this.dispose();
                // 下次打开回到纯终端状态，不让上次的面板状态残留
                this.filePanelVisible = false;
            }
        }
    },
    beforeUnmount() {
        this.dispose();
    },
    methods: {
        // ---------- 生命周期 ----------
        start() {
            this.createTerminal();
            this.bindResize();
            this.connect();
        },

        createTerminal() {
            if (!this.$refs.termHost || this.term) {
                return;
            }
            this.term = new Terminal({
                cursorBlink: true,
                fontSize: 13,
                lineHeight: 1.2,
                scrollback: 5000,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                theme: {
                    background: '#1e1e1e',
                    foreground: '#d4d4d4'
                }
            });
            this.fitAddon = new FitAddon();
            this.term.loadAddon(this.fitAddon);
            // 用户敲键 -> 发 input 帧
            this.term.onData(data => this.send({ type: 'input', data }));
            this.term.open(this.$refs.termHost);
            this.term.focus();
            this.fit();
        },

        dispose() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
            if (this.fitTimer) {
                clearTimeout(this.fitTimer);
                this.fitTimer = null;
            }
            // 先摘掉回调再关闭，避免 dispose 过程又被 onclose 打扰
            if (this.ws) {
                const ws = this.ws;
                this.ws = null;
                ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
                try {
                    ws.close();
                } catch (ex) {
                    // ignore
                }
            }
            if (this.term) {
                try {
                    this.term.dispose();
                } catch (ex) {
                    // ignore
                }
                this.term = null;
                this.fitAddon = null;
            }
            this.recording = false;
            this.status = 'closed';
        },

        // ---------- 连接 ----------
        connect() {
            if (!this.token) {
                this.writeLine('未登录或登录已失效，请先登录', '31');
                return;
            }
            const base = (config() && config().baseServer) || '';
            const wsBase = base.replace(/^http/i, 'ws').replace(/\/+$/, '');
            const cols = (this.term && this.term.cols) || 120;
            const rows = (this.term && this.term.rows) || 30;
            const url = `${wsBase}/openapi/ws/ssh?token=${encodeURIComponent(this.token)}`
                + `&machineId=${encodeURIComponent(this.machine.id)}&cols=${cols}&rows=${rows}`;

            this.status = 'connecting';
            this.writeLine(`正在连接 ${this.machine.name}（${this.machine.address}）…`, '90');

            const ws = new WebSocket(url);
            // 必须设成 arraybuffer，否则二进制帧会变成 Blob，写进终端会报错
            ws.binaryType = 'arraybuffer';
            this.ws = ws;

            ws.onopen = () => {
                this.status = 'connected';
                // 抽屉展开后的真实尺寸和连接前的可能不同，连上后立刻同步一次
                this.fit();
            };
            ws.onmessage = event => this.onMessage(event);
            ws.onerror = () => this.writeLine('连接异常，请检查网络或后端服务', '31');
            ws.onclose = event => {
                this.status = 'closed';
                this.writeLine(`已断开（code=${event.code}）`, '33');
            };
        },

        onMessage(event) {
            // 二进制帧 = SSH 原始输出，原样写进终端（不做任何字符串转换）
            if (event.data instanceof ArrayBuffer) {
                this.term.write(new Uint8Array(event.data));
                return;
            }
            if (typeof event.data !== 'string') {
                return;
            }
            let msg;
            try {
                msg = JSON.parse(event.data);
            } catch (ex) {
                return;
            }
            switch (msg.type) {
                case 'ready':
                    this.writeLine(`已连接 ${msg.machineName}  ${msg.cols}×${msg.rows}`, '32');
                    this.recording = !!msg.recording;
                    if (this.recording) {
                        this.writeLine('[提示] 本次会话的终端输出将被记录，用于审计与问题追溯', '33');
                    }
                    break;
                case 'error':
                    this.writeLine(`[错误] ${msg.message}`, '31');
                    ElMessage.error(msg.message);
                    break;
                case 'exit':
                    this.writeLine(`[会话结束] ${msg.reason}`, '33');
                    break;
                case 'warn':
                    this.writeLine(`[提示] ${msg.message}`, '33');
                    break;
                case 'ping':
                case 'pong':
                    // 心跳：静默处理
                    break;
                default:
                    break;
            }
        },

        reconnect() {
            this.dispose();
            this.$nextTick(() => this.start());
        },

        // ---------- 尺寸同步 ----------
        bindResize() {
            if (this.resizeObserver || !this.$refs.termHost) {
                return;
            }
            // 用 ResizeObserver 覆盖所有尺寸变化场景：抽屉展开动画、窗口缩放、全屏切换
            this.resizeObserver = new ResizeObserver(() => this.scheduleFit());
            this.resizeObserver.observe(this.$refs.termHost);
        },

        scheduleFit() {
            if (this.fitTimer) {
                clearTimeout(this.fitTimer);
            }
            this.fitTimer = setTimeout(() => {
                this.fitTimer = null;
                this.fit();
            }, 80);
        },

        fit() {
            if (!this.term || !this.fitAddon) {
                return;
            }
            try {
                this.fitAddon.fit();
            } catch (ex) {
                // 抽屉未显示 / 容器尺寸为 0 时 fit 会抛错，忽略
                return;
            }
            this.send({ type: 'resize', cols: this.term.cols, rows: this.term.rows });
        },

        // ---------- 工具 ----------
        send(obj) {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify(obj));
            }
        },

        writeLine(text, colorCode) {
            if (!this.term) {
                return;
            }
            this.term.write(`\x1b[${colorCode}m${text}\x1b[0m\r\n`);
        },

        /**
         * 关闭前确认。
         * el-drawer 的 before-close 会被「关闭」按钮和 Esc 共同触发，
         * 所以放在这里一处即可覆盖所有关闭入口（点遮罩已在模板里禁用）。
         */
        handleBeforeClose(done) {
            ElMessageBox.confirm(
                '关闭后，远端正在运行的前台任务会被终止（未用 nohup / tmux 启动的任务会中断），确定关闭吗？',
                '关闭控制台',
                {
                    confirmButtonText: '确定关闭',
                    cancelButtonText: '取消',
                    type: 'warning'
                }
            )
                .then(() => done())
                .catch(() => {
                    // 取消：什么都不做，抽屉保持打开
                });
        }
    }
};
</script>

<!-- 抽屉会挂到 body 下，样式需放在非 scoped 块并统一带 .ssh-console-drawer 前缀 -->
<style lang="scss">
.ssh-console-drawer {
    overflow: hidden;
    border-radius: 16px 16px 0 0;
}

.ssh-console-drawer .el-drawer__header {
    gap: 12px;
    margin-bottom: 0;
    padding: 10px 16px;
    border-bottom: 1px solid #eef1f6;
}

.ssh-console-drawer .el-drawer__title {
    margin: 0;
}

/* 抽屉主体：横向分栏（终端 + 可选的文件面板），终端要占满整个 body */
.ssh-console-drawer .el-drawer__body {
    display: flex;
    padding: 0;
    overflow: hidden;
    background-color: #1e1e1e;
}

.ssh-console-drawer .ssh-console-body {
    display: flex;
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
}

.ssh-console-drawer .ssh-console-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.ssh-console-drawer .ssh-console-head__machine {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
}

.ssh-console-drawer .ssh-console-head__addr {
    font-size: 13px;
    color: #8a94a6;
}

/* 录制中提示：常驻可见，让使用者对"会话被记录"保持知情 */
.ssh-console-drawer .ssh-console-head__rec {
    padding: 2px 10px;
    border-radius: 999px;
    background-color: #fef0f0;
    color: #f56c6c;
    font-size: 12px;
    font-weight: 600;
}

/* 连接状态徽标：三种状态三种颜色 */
.ssh-console-drawer .ssh-console-head__status {
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
}

.ssh-console-drawer .ssh-console-head__status.is-connected {
    background-color: #f0f9eb;
    color: #67c23a;
}

.ssh-console-drawer .ssh-console-head__status.is-connecting {
    background-color: #fdf6ec;
    color: #e6a23c;
}

.ssh-console-drawer .ssh-console-head__status.is-closed {
    background-color: #fef0f0;
    color: #f56c6c;
}

.ssh-console-drawer .ssh-console-head__ops {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
}

.ssh-console-drawer .ssh-console-btn {
    margin-left: 0;
    border-radius: 9px;
}

/* 文件按钮的展开态：一眼能看出面板当前是开着的 */
.ssh-console-drawer .ssh-console-btn.is-on {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
    border-color: var(--el-color-primary-light-5);
}

.ssh-console-drawer .ssh-console-btn__icon {
    margin-right: 4px;
}

/* 终端容器：占满剩余宽度。xterm 按这个尺寸算行列，
   文件面板展开/收起引起的宽度变化由 ResizeObserver 自动同步给 PTY */
.ssh-console-drawer .ssh-console-term {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    padding: 6px 8px;
    box-sizing: border-box;
}
</style>
